import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import UserContext from '../UserContext';
import Pricing from '../components/Pricing';
import { Alert, AlertTitle, Box, Button, Chip, Divider, LinearProgress, Link, Paper, Stack, Typography } from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SimpleLoading } from '../components/Loading';
import SupportLink from '../components/SupportLink';
import StripeButton from '../components/StripeButton';
import { acceptInvite, createPortalSession, declineInvite, getPendingInvite, PendingInvite, PortalSessionResponse } from '../rest/BackendApi';
import { getConnectionUsage, isBillingAdmin } from '../business/Subscription';
import { PageContainer, PageHeader, Section } from '../components/Page';
import { displayDate } from '../util/TimeUtil';

const PORTAL_REDIRECT = '/api/subscription/portal-session-redirect';

const STATUS_CHIP: Record<Subscription['status'], { label: string; color: 'success' | 'info' | 'error' }> = {
  ACTIVE: { label: 'Active', color: 'success' },
  TRIAL: { label: 'Trial', color: 'info' },
  CANCELLED: { label: 'Expired', color: 'error' },
};

const USAGE_COLOR = { ok: 'primary', warning: 'warning', over: 'error' } as const;

// Why the backend sent someone back here instead of to Stripe
const STRIPE_PROBLEMS: Record<string, { severity: 'info' | 'warning' | 'error'; text: React.ReactNode }> = {
  'already-on-plan': { severity: 'info', text: "You're already on that plan." },
  'unknown-plan': {
    severity: 'warning',
    text: (
      <>
        That plan isn't available any more. Pick another plan, or <SupportLink text="contact support" />.
      </>
    ),
  },
  'stripe-error': {
    severity: 'error',
    text: (
      <>
        We couldn't open Stripe. Please try again in a moment, or <SupportLink text="contact support" />.
      </>
    ),
  },
};

function SubscriptionLayout(props: PropsWithChildren<{ wide?: boolean }>) {
  const [searchParams] = useSearchParams();
  const problem = STRIPE_PROBLEMS[searchParams.get('problem') || ''];
  // The plan cards wrap to fit the width, so give them the full page when they're shown.
  return (
    <PageContainer maxWidth={props.wide ? 'none' : 1100} dense={props.wide}>
      <PageHeader title="Subscription" description="Your BAS Ready plan, billing and Xero connection usage." />
      {problem && (
        <Alert severity={problem.severity} sx={{ mb: 2 }}>
          {problem.text}
        </Alert>
      )}
      {props.children}
    </PageContainer>
  );
}

export function ConnectionUsage(props: { connectionCount: number; connectionLimit: number }) {
  const { connectionCount, connectionLimit } = props;
  const usage = getConnectionUsage(connectionCount, connectionLimit);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Xero connections
        </Typography>
        <Typography variant="body2">
          <b>{connectionCount}</b> of {connectionLimit} used
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={usage.percent}
        color={USAGE_COLOR[usage.level]}
        aria-label="Xero connections used"
        sx={{ height: 8, borderRadius: 4 }}
      />
      {usage.level === 'warning' && (
        <Typography variant="body2" sx={{ mt: 1, color: '#8a4b00' }}>
          You're close to your plan's limit. Upgrade before connecting more organisations.
        </Typography>
      )}
    </Box>
  );
}

// Someone without a firm may have been invited to one. They only join once they accept here.
export function PendingInviteBanner(props: { invite: PendingInvite; onDeclined: () => void }) {
  const { invite, onDeclined } = props;
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const respond = async (accept: boolean) => {
    setBusy(true);
    setFailed(false);
    try {
      if (accept) {
        await acceptInvite(invite.id);
        window.location.href = '/dashboard';
        return;
      }
      await declineInvite(invite.id);
      onDeclined();
    } catch (e) {
      setFailed(true);
    }
    setBusy(false);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: { xs: 1, sm: 3 } }}>
      <Paper variant="outlined" sx={{ maxWidth: 520, width: '100%', p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: '#e6f3f7',
            color: '#007ca3',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <GroupAddIcon />
        </Box>
        <Typography component="h2" sx={{ fontSize: '1.3rem', fontWeight: 700 }}>
          You're invited to join {invite.firmName}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          <b>{invite.invitedByName}</b> invited you to their firm on BAS Ready.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5, p: 2, bgcolor: '#f6f8fa', borderRadius: 2 }}>
          {invite.firmOpenAccess ? (
            <VisibilityOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mt: '2px' }} />
          ) : (
            <LockOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mt: '2px' }} />
          )}
          <Typography variant="body2" color="text.secondary">
            {invite.firmOpenAccess
              ? "This firm has open access on, so everyone in the firm will see the Xero organisations you've connected."
              : "This firm has open access off, so people in the firm only see organisations they've connected themselves. If the firm turns it on, everyone will see yours too."}
          </Typography>
        </Box>

        {failed && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Something went wrong. Please try again or <SupportLink text="contact support" />.
          </Alert>
        )}

        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} gap={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button disabled={busy} onClick={() => respond(false)}>
            Decline
          </Button>
          <Button variant="contained" disabled={busy} onClick={() => respond(true)}>
            Accept invite
          </Button>
        </Stack>

        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="text.secondary">
          Want your own subscription instead? Decline the invite to see the plans.
        </Typography>
      </Paper>
    </Box>
  );
}

// Without a firm: show the pending invite if there is one, otherwise the plans
function NoFirm() {
  // undefined while we check, so the plans don't flash up before the invite
  const [invite, setInvite] = useState<PendingInvite | null | undefined>(undefined);

  useEffect(() => {
    getPendingInvite()
      .then(setInvite)
      .catch(() => setInvite(null));
  }, []);

  if (invite === undefined) {
    return null;
  }

  if (invite) {
    return <PendingInviteBanner invite={invite} onDeclined={() => setInvite(null)} />;
  }

  return (
    <>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 600 }}>Choose a plan</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        You don't have a subscription yet. If you've just subscribed in Stripe, it can take a couple of minutes to show here. Try refreshing, or{' '}
        <SupportLink text="contact support" />.
      </Typography>
      <Pricing mode="subscribe" />
    </>
  );
}

export default function Subscription() {
  const userContext = useContext(UserContext);
  const user = userContext?.user;
  const [searchParams] = useSearchParams();
  const [portalSession, setPortalSession] = useState<PortalSessionResponse | null>(null);
  // The XPM page links here to show the plans straight away
  const [showPlans, setShowPlans] = useState(searchParams.has('showPricing'));

  useEffect(() => {
    if (user && isBillingAdmin(user) && user.firm?.subscription?.status === 'CANCELLED' && portalSession == null) {
      createPortalSession()
        .then(session => setPortalSession(session))
        .catch(() => setPortalSession({ hasStripeSubscription: false }));
    }
  }, [user]);

  if (user == null) {
    // App shows a full-page spinner while the user loads (and redirects to login if there is none),
    // so don't add a second one here.
    return <SubscriptionLayout />;
  }

  if (user.firm == null) {
    return (
      <SubscriptionLayout wide>
        <NoFirm />
      </SubscriptionLayout>
    );
  }

  const firm = user.firm;
  const subscription = firm.subscription;
  const billingAdmin = isBillingAdmin(user);
  const status = STATUS_CHIP[subscription.status] || STATUS_CHIP.ACTIVE;

  if (subscription.status === 'CANCELLED') {
    let action: React.ReactNode;
    if (!billingAdmin) {
      action = (
        <>
          Ask your billing admin (<Link href={`mailto:${firm.billingUser.email}`}>{firm.billingUser.email}</Link>) to renew the subscription.
        </>
      );
    } else if (portalSession == null) {
      action = <SimpleLoading message="Checking your billing details" />;
    } else if (portalSession.url == undefined) {
      action = (
        <>
          We couldn't find your billing details in Stripe. Please <SupportLink text="contact support" /> to renew.
        </>
      );
    } else if (portalSession.hasStripeSubscription) {
      // Stripe still has the subscription, waiting on a payment: fixing the payment brings it back
      action = (
        <StripeButton variant="contained" href={portalSession.url} endIcon={<OpenInNewIcon />}>
          Update payment details in Stripe
        </StripeButton>
      );
    } else {
      action = <Pricing mode="renew" currentLevel={subscription.level} />;
    }

    return (
      <SubscriptionLayout wide={billingAdmin}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Your subscription has expired</AlertTitle>
          Syncing with Xero is paused until the subscription is renewed.
        </Alert>
        <Section title="Renew your plan" description={`Previous plan: ${subscription.levelName}. Pick any plan to start again.`}>
          <Box>{action}</Box>
        </Section>
      </SubscriptionLayout>
    );
  }

  const over = firm.connectionCount > subscription.connectionLimit;

  return (
    <SubscriptionLayout wide={showPlans}>
      {over && (
        // Syncing carries on, the limit only stops new organisations being connected (XeroSyncService).
        // Removing extra connections after a month isn't automatic: it's what we may do if firms stay over.
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Connection limit reached</AlertTitle>
          Your firm is using more Xero connections than your plan allows, so you can't connect more organisations. Either{' '}
          <Link component={RouterLink} to="/organisations">
            disconnect some organisations
          </Link>
          {billingAdmin ? (
            <>
              {' '}
              or{' '}
              <Link component="button" onClick={() => setShowPlans(true)} sx={{ verticalAlign: 'baseline' }}>
                upgrade your plan
              </Link>
              .
            </>
          ) : (
            <> or ask your billing admin to upgrade the plan.</>
          )}{' '}
          If your firm stays over the limit for more than a month, the extra connections may be removed.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Section
          title="Plan"
          actions={
            <Stack direction="row" gap={1}>
              <Button size="small" variant={showPlans ? 'text' : 'contained'} onClick={() => setShowPlans(!showPlans)}>
                {showPlans ? 'Hide plans' : billingAdmin ? 'Change plan' : 'See plans'}
              </Button>
              {billingAdmin && (
                <StripeButton size="small" variant="outlined" href={PORTAL_REDIRECT} endIcon={<OpenInNewIcon />}>
                  Manage billing
                </StripeButton>
              )}
            </Stack>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }}>{subscription.levelName}</Typography>
            <Chip size="small" label={status.label} color={status.color} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Up to {subscription.connectionLimit} Xero connections
          </Typography>
          {subscription.status === 'TRIAL' && subscription.trialEndDate && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Trial ends {displayDate(subscription.trialEndDate)}
            </Typography>
          )}
          {!billingAdmin && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Billing is managed by {firm.billingUser.email}.
            </Typography>
          )}
        </Section>

        <Section title="Usage">
          <ConnectionUsage connectionCount={firm.connectionCount} connectionLimit={subscription.connectionLimit} />
        </Section>
      </Box>

      {showPlans && (
        <Box sx={{ mt: 1 }}>
          <Typography component="h2" sx={{ fontSize: '1.05rem', fontWeight: 600, mb: 2 }}>
            {billingAdmin ? 'Change plan' : 'Plans'}
          </Typography>
          <Pricing mode={billingAdmin ? 'change' : 'view'} currentLevel={subscription.level} />
        </Box>
      )}
    </SubscriptionLayout>
  );
}
